import React, { useState, useMemo, useEffect } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { deliveryDetailsFilters } from "../filterConfigurations";
import { formatDateTime } from "../../../utils/formatDate";

const DeliveryDetails = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Delivery Details"]?.edit;
  const {
    data: deliveryDetails,
    loading,
    error,
  } = useFirestore("DeliveryDetails");

  // Fetch HEI Programs for lookup
  const {
    data: heiPrograms,
    loading: heiLoading,
    error: heiError,
  } = useFirestore("HeiPrograms");

  // Fetch Users for instructor lookup
  const {
    data: users,
    loading: usersLoading,
    error: usersError,
  } = useFirestore("users");

  // Fetch Instructor Archive for inactive instructor lookup
  const {
    data: instructorArchive,
    loading: archiveLoading,
    error: archiveError,
  } = useFirestore("InstructorArchive");
  const [selectedDeliveryDetail, setSelectedDeliveryDetail] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Delivery Details";

  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") return "No";
    if (value === -1 || value === "-1") return "Yes";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  // Function to get instructor name from ID (checks both active and inactive instructors)
  const getInstructorName = (instructorId) => {
    if (!instructorId) return "";

    // First check active users collection - try both InstructorID field and document ID
    if (users) {
      const activeInstructor = users.find((user) => {
        if (!user.userTypes?.instructor) return false;

        // Try matching by InstructorID field first
        if (
          user.InstructorID &&
          String(user.InstructorID) === String(instructorId)
        ) {
          return true;
        }

        // Fallback to document ID matching
        if (String(user.id) === String(instructorId)) {
          return true;
        }

        return false;
      });

      if (activeInstructor) {
        return activeInstructor.name || "Unknown Name";
      }
    }

    // Then check instructor archive collection - match by InstructorID field
    if (instructorArchive) {
      const archivedInstructor = instructorArchive.find(
        (instructor) =>
          String(instructor.InstructorID) === String(instructorId),
      );

      if (archivedInstructor) {
        const name =
          archivedInstructor.name ||
          archivedInstructor.instructor_name ||
          "Unknown Name";
        return `${name} (Inactive)`;
      }
    }

    // Debug logging for unmatched IDs
    console.warn(
      `Instructor ID ${instructorId} not found in either collection`,
    );
    if (users && instructorArchive) {
      console.warn(
        "Available active instructor IDs:",
        users.filter((u) => u.userTypes?.instructor).map((u) => u.InstructorID),
      );
      console.warn(
        "Available archived instructor IDs:",
        instructorArchive.map((i) => i.InstructorID),
      );
    }

    return instructorId; // Return the ID if instructor not found anywhere
  };

  // Create enhanced data with instructor names for search
  const enhancedDeliveryDetails = useMemo(() => {
    if (!deliveryDetails) return [];
    return deliveryDetails.map((item) => ({
      ...item,
      instructorName: getInstructorName(item.InstructorID),
    }));
  }, [deliveryDetails, users, instructorArchive]);

  // Create dynamic filter configuration with the Instructor dropdown options
  const dynamicFilters = useMemo(() => {
    const filters = [...deliveryDetailsFilters];

    const instructorFilterIndex = filters.findIndex(
      (f) => f.name === "InstructorID",
    );
    if (instructorFilterIndex !== -1) {
      const activeOptions = (users || [])
        .filter((user) => user.userTypes?.instructor)
        .map((user) => ({
          value: String(user.InstructorID || user.id),
          label: user.name || "Unknown Name",
        }));
      const activeIds = new Set(activeOptions.map((opt) => opt.value));

      const inactiveOptions = (instructorArchive || [])
        .filter(
          (instructor) =>
            instructor.InstructorID &&
            !activeIds.has(String(instructor.InstructorID)),
        )
        .map((instructor) => ({
          value: String(instructor.InstructorID),
          label: `${
            instructor.name || instructor.instructor_name || "Unknown Name"
          } (Inactive)`,
        }));

      activeOptions.sort((a, b) => a.label.localeCompare(b.label));
      inactiveOptions.sort((a, b) => a.label.localeCompare(b.label));

      filters[instructorFilterIndex] = {
        ...filters[instructorFilterIndex],
        options: [...activeOptions, ...inactiveOptions],
      };
    }

    return filters;
  }, [users, instructorArchive]);

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(enhancedDeliveryDetails || [], dynamicFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Function to get HEI program name from code
  const getHeiProgramName = (programCode) => {
    if (!programCode || !heiPrograms) return programCode || "";

    const program = heiPrograms.find(
      (p) =>
        p.ProgramCode === programCode ||
        p.HEI_Program_Code === programCode ||
        p.HEIProgramCode === programCode,
    );

    if (program) {
      const programName =
        program.HEIProgramName || program.Program_Name || program.ProgramName;
      return programName ? `${programName} (${programCode})` : programCode;
    }

    return programCode;
  };

  const columns = useMemo(
    () => [
      {
        field: "ProgramDeliveryID",
        headerName: "Program Delivery ID",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "DeliveryDetailsID",
        headerName: "Delivery Details ID",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "HEIProgramCode",
        headerName: "HEI Program Code",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => getHeiProgramName(params.value),
      },
      {
        field: "InstructorID",
        headerName: "Instructor",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const instructorName = getInstructorName(params.value);
          if (instructorName === params.value && params.value) {
            // Check if user exists but is not an instructor
            const userExists = users?.find((user) => user.id === params.value);
            if (userExists) {
              return `${instructorName} (exists but not instructor)`;
            } else {
              return `${instructorName} (ID not found)`;
            }
          }
          return instructorName;
        },
      },
      {
        field: "CTE_Program_Cd",
        headerName: "CTE Program Code",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "MVTPCstrand",
        headerName: "MVTPC Strand",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "StrandNonactive",
        headerName: "Strand Nonactive",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "InstructorNonactiveInStrand",
        headerName: "Instructor Nonactive In Strand",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "SRGradClass",
        headerName: "SR Grad Class",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "JRclass",
        headerName: "JR Class",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => formatDateTime(params.value),
        sortComparator: (v1, v2) => {
          const aDate = v1 && v1.seconds ? v1.seconds : 0;
          const bDate = v2 && v2.seconds ? v2.seconds : 0;
          return aDate - bDate;
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => formatDateTime(params.value),
        sortComparator: (v1, v2) => {
          const aDate = v1 && v1.seconds ? v1.seconds : 0;
          const bDate = v2 && v2.seconds ? v2.seconds : 0;
          return aDate - bDate;
        },
      },
    ],
    [heiPrograms],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((detail, index) => ({
      id: detail.id || index, // Use detail.id if available, otherwise use index
      ...detail,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedDeliveryDetail(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedDeliveryDetail(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  // Utility function to analyze instructor ID issues
  const analyzeInstructorIssues = () => {
    if (!deliveryDetails || !users) return;

    const issues = [];
    const uniqueInstructorIds = [
      ...new Set(
        deliveryDetails.map((item) => item.InstructorID).filter(Boolean),
      ),
    ];

    uniqueInstructorIds.forEach((instructorId) => {
      const user = users.find((u) => u.id === instructorId);
      if (!user) {
        issues.push({
          id: instructorId,
          type: "USER_NOT_FOUND",
          message: `User with ID ${instructorId} does not exist in users collection`,
        });
      } else if (!user.userTypes?.instructor) {
        issues.push({
          id: instructorId,
          type: "NOT_INSTRUCTOR",
          message: `User ${user.name} (${instructorId}) exists but is not marked as instructor`,
          user: user,
        });
      }
    });

    if (issues.length > 0) {
      console.group("Instructor ID Issues Analysis");
      issues.forEach((issue) => {
        console.warn(`${issue.type}: ${issue.message}`, issue.user || "");
      });
      console.groupEnd();
    }

    return issues;
  };

  // Run analysis when data is loaded (for debugging)
  useEffect(() => {
    if (
      deliveryDetails &&
      users &&
      deliveryDetails.length > 0 &&
      users.length > 0
    ) {
      const issues = analyzeInstructorIssues();
      if (issues && issues.length > 0) {
        console.log(
          `Found ${issues.length} instructor ID issues. Check console for details.`,
        );
      }
    }
  }, [deliveryDetails, users]);

  if (loading || heiLoading || usersLoading || archiveLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || heiError || usersError || archiveError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching data:{" "}
          {typeof (error || heiError || usersError) === "string"
            ? error || heiError || usersError
            : (error || heiError || usersError)?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={dynamicFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Delivery Details Filters"
          resultCount={filteredData?.length || 0}
          totalCount={enhancedDeliveryDetails?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          </Box>
          {canEdit && (
            <EditUpload
              collectionName="DeliveryDetails"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={deliveryDetails}
              templateFields={Array.from(
                new Set(
                  [
                    ...columns.map((col) => col.field),
                    "Notes",
                    "Career_Field_Cd",
                  ].filter(
                    (field) => field !== "created_at" && field !== "updatedAt",
                  ),
                ),
              )}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="DeliveryDetails" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: () => (
                <SimpleToolbar
                  exportColumnOrder={columns.map((col) => col.field)}
                  rows={rows}
                  onBulkActionComplete={handleBulkActionComplete}
                  pageName={pageName}
                />
              ),
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedDeliveryDetail && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          deliveryDetail={selectedDeliveryDetail}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeliveryDetails;
